import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, Search, Plus, Edit, Trash2, Box, AlertTriangle, AlertCircle, CheckCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Shield, Layers, Gauge, FileText, Clock, Check, PauseCircle, Barcode, TrendingUp, Activity, Tag } from 'lucide-react';
import InventaireListPageShell from './components/InventaireListPageShell';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import { InventaireModalPortal, InventaireViewModalPortal } from './components/inventaireModalPortal';
import FacturationFormModalTabs from '../facturation/components/FacturationFormModalTabs';
import {
  sliceQawaemStats,
  buildInventaireBulkDeleteFilterAction,
  InventaireTableSelectAllHeader,
  InventaireTableRowCheckbox,
  INVENTAIRE_TABLE_ACTIONS_TH_CLASS,
  buildInventaireImportExportProps,
  buildInventaireColumnVisibilityState,
  buildInventaireQawaemColumnDefs,
  isInventaireColumnVisible,
  inventaireTableColSpan,
  nextInventaireEntityRef,
  InventoryConfigNotice,
  useInventaireListSettings,
  sliceInventaireListItems,
  inventaireRecordInDateRange,
  INVENTAIRE_FORM_GRID,
  fetchInventaireWarehouses,
  fetchInventaireSuppliers,
  buildInventaireWarehouseSelectOptions,
  buildInventaireProductSelectOptions,
  buildInventaireSupplierSelectOptions,
  mapInventaireWarehouseSelection,
  mapInventaireSupplierSelection,
} from './inventaireListPageUtils';
import { useInventoryConfig } from './useInventoryConfig';
import { useArticlesConfig } from '../articles/useArticlesConfig';
import { fetchCatalogProducts } from '../articles/catalogProductOptions';
import { useSettings } from '../../core/contexts/SettingsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { useTranslation } from 'react-i18next';
import { getBrandLocale } from '../../shared/utils/brandCurrency';
import { getBrandCountryOptions } from '../../shared/utils/brandCountry';

const LotsTracabilitePage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.inventaire.pages.lotsTracabilitePage.${key}`, opts);
  const countryLabel = (key) => t(`modules.inventaire.pages.lotsTracabilitePage.${key}`);
  const { isDashboardLightMode } = useGlobalTheme();
  const dateLocale = getBrandLocale(i18n.language);
  const formatDate = (value) => (value ? new Date(value).toLocaleDateString(dateLocale) : p('na'));
  const { settings } = useSettings();
  const inventoryConfig = useInventoryConfig();
  const { dateRange, viewMode, setViewMode, itemsPerPage } = useInventaireListSettings(inventoryConfig);
  const expiryAlertDays = inventoryConfig.expiryAlertDays ?? 30;
  const originCountryOptions = useMemo(
    () => [
      { value: '', label: p('placeholder_select_country') },
      ...getBrandCountryOptions(countryLabel),
    ],
    [t, i18n.language]
  );
  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Status dropdown
      const isClickOnStatusButton = event.target.closest('[data-lot-status-dropdown-button]');
      const isClickInStatusDropdown = event.target.closest('[data-lot-status-dropdown]');
      if (!isClickOnStatusButton && !isClickInStatusDropdown) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [searchTerm, setSearchTerm] = useState('');
  const [lots, setLots] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  
  // Collapsible sections state
  const [lotFormTab, setLotFormTab] = useState('basicInfo');

  const lotFormTabs = useMemo(() => ([
    { id: 'basicInfo', label: p('section_identification'), icon: Barcode, iconColor: 'text-emerald-400' },
    { id: 'traceabilityInfo', label: p('section_traceability'), icon: Activity, iconColor: 'text-purple-400' },
  ]), [p]);

  
  const [formData, setFormData] = useState({ 
    lot_number: '', 
    product_id: '', 
    product_name: '',
    quantity: '',
    unit: 'kg',
    manufacturing_date: null,
    expiry_date: null,
    supplier_id: '',
    supplier_name: '',
    reception_date: null,
    location_id: '',
    status: 'active',
    quality_status: 'approved',
    batch_number: '',
    serial_number: '',
    certificate_number: '',
    origin_country: '',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_lots');
  const [quickFilter, setQuickFilter] = useState('all');
  
  // PDF and Excel export states
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);

  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  const getQualityStatusLabel = (status) => {
    const map = {
      approved: p('quality_approved'),
      pending: p('quality_pending'),
      rejected: p('quality_rejected'),
      quarantine: p('quality_quarantine')
    };
    return map[status] || status || p('na');
  };

  // Multi-column sorting state

  useEffect(() => { 
    loadLots(); 
    loadProducts();
    loadSuppliers();
    loadWarehouses();
    loadLocations();
  }, []);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) setShowStatusDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadLots = async () => {
    try {
      const response = await localApiAdapter.get('/inventory/lots');
      setLots(sortRecordsNewestFirst(response?.data || response || []));
    } catch (error) {
      console.error('Error loading lots:', error);
      setLots([]);
    }
  };

  const loadProducts = async () => {
    try {
      setProducts(await fetchCatalogProducts());
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    }
  };

  const loadSuppliers = async () => {
    try {
      setSuppliers(await fetchInventaireSuppliers());
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setSuppliers([]);
    }
  };

  const loadWarehouses = async () => {
    try {
      setWarehouses(await fetchInventaireWarehouses());
    } catch (error) {
      console.error('Error loading warehouses:', error);
      setWarehouses([]);
    }
  };

  const loadLocations = async () => {
    try {
      const response = await localApiAdapter.get('/inventory/locations');
      setLocations(response?.data || response || []);
    } catch (error) {
      console.error('Error loading locations:', error);
      setLocations([]);
    }
  };

  const stats = {
    total: lots.length,
    active: lots.filter(l => l.status === 'active').length,
    expired: lots.filter(l => l.status === 'expired').length,
    recalled: lots.filter(l => l.status === 'recalled').length,
    expiringToday: lots.filter(l => {
      if (!l.expiry_date) return false;
      const today = new Date();
      const expiry = new Date(l.expiry_date);
      const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
      return diffDays >= 0 && diffDays <= expiryAlertDays;
    }).length,
    totalQuantity: lots.reduce((sum, l) => sum + (parseFloat(l.quantity) || 0), 0)
  };

  const mainTabs = [
    { id: 'all_lots', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_statut', name: p('tab_by_status'), icon: Activity },
    { id: 'par_expiration', name: p('tab_by_expiration'), icon: Clock }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'active', name: p('filter_active'), icon: CheckCircle },
    { id: 'expiring', name: p('filter_expiring'), icon: AlertTriangle },
    { id: 'expired', name: p('filter_expired'), icon: XCircle }
  ];

  const lotDistribution = {
    all_lots: {
      title: p('overview_title'),
      data: [
        { filterKey: 'active', name: p('dist_active'), count: stats.active, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: 'expiring30', name: p('dist_expiring_30'), count: stats.expiringToday, color: "bg-gradient-to-r from-yellow-500 to-yellow-600" },
        { filterKey: 'expired', name: p('dist_expired'), count: stats.expired, color: "bg-gradient-to-r from-red-500 to-red-600" },
        { filterKey: 'recalled', name: p('dist_recalled'), count: stats.recalled, color: "bg-gradient-to-r from-orange-500 to-orange-600" }
      ]
    },
    par_statut: {
      title: p('distribution_by_status'),
      data: [
        { filterKey: 'active', name: p('dist_active'), count: stats.active, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: 'expired', name: p('dist_expired'), count: stats.expired, color: "bg-gradient-to-r from-red-500 to-red-600" },
        { filterKey: 'recalled', name: p('dist_recalled'), count: stats.recalled, color: "bg-gradient-to-r from-orange-500 to-orange-600" }
      ]
    },
    par_expiration: {
      title: p('distribution_by_expiration'),
      data: [
        { filterKey: 'lt7', name: p('dist_lt_7'), count: lots.filter(l => {
          if (!l.expiry_date) return false;
          const today = new Date();
          const expiry = new Date(l.expiry_date);
          const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
          return diffDays >= 0 && diffDays < 7;
        }).length, color: "bg-gradient-to-r from-red-500 to-red-600" },
        { filterKey: 'd7_30', name: p('dist_7_30'), count: lots.filter(l => {
          if (!l.expiry_date) return false;
          const today = new Date();
          const expiry = new Date(l.expiry_date);
          const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
          return diffDays >= 7 && diffDays <= expiryAlertDays;
        }).length, color: "bg-gradient-to-r from-yellow-500 to-yellow-600" },
        { filterKey: 'gt30', name: p('dist_gt_30'), count: lots.filter(l => {
          if (!l.expiry_date) return false;
          const today = new Date();
          const expiry = new Date(l.expiry_date);
          const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
          return diffDays > expiryAlertDays;
        }).length, color: "bg-gradient-to-r from-green-500 to-green-600" }
      ]
    }
  };

  const distributionInfo = lotDistribution[mainViewMode] || lotDistribution.all_lots;

  const baseFilteredLots = lots.filter(l => {
    if (!inventaireRecordInDateRange(dateRange, l.expiry_date, l.reception_date, l.manufacturing_date, l.created_at)) return false;

    if (searchTerm && !l.lot_number?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !l.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !l.batch_number?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    if (mainViewMode === 'all_lots') {
      if (quickFilter === 'active' && l.status !== 'active') return false;
      if (quickFilter === 'expired' && l.status !== 'expired') return false;
      if (quickFilter === 'expiring') {
        if (!l.expiry_date) return false;
        const today = new Date();
        const expiry = new Date(l.expiry_date);
        const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
        if (diffDays < 0 || diffDays > expiryAlertDays) return false;
      }
    }
    
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_lots':
          if (activeDistributionFilter === 'active' && l.status !== 'active') return false;
          if (activeDistributionFilter === 'expired' && l.status !== 'expired') return false;
          if (activeDistributionFilter === 'recalled' && l.status !== 'recalled') return false;
          if (activeDistributionFilter === 'expiring30') {
            if (!l.expiry_date) return false;
            const today = new Date();
            const expiry = new Date(l.expiry_date);
            const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
            if (diffDays < 0 || diffDays > expiryAlertDays) return false;
          }
          break;
        case 'par_statut':
          if (activeDistributionFilter === 'active' && l.status !== 'active') return false;
          if (activeDistributionFilter === 'expired' && l.status !== 'expired') return false;
          if (activeDistributionFilter === 'recalled' && l.status !== 'recalled') return false;
          break;
        case 'par_expiration':
          if (!l.expiry_date) return false;
          const today = new Date();
          const expiry = new Date(l.expiry_date);
          const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
          if (activeDistributionFilter === 'lt7' && (diffDays < 0 || diffDays >= 7)) return false;
          if (activeDistributionFilter === 'd7_30' && (diffDays < 7 || diffDays > expiryAlertDays)) return false;
          if (activeDistributionFilter === 'gt30' && diffDays <= expiryAlertDays) return false;
          break;
      }
    }
    
    return true;
  });

  const columnFilterRules = useMemo(() => [
    textColumnRule('lot_number'),
    textColumnRule('product_name'),
    textColumnRule('quantity', (r) => String(r.quantity ?? '')),
    { key: 'expiry_date', getValue: (r) => r.expiry_date ? new Date(r.expiry_date).toLocaleDateString(dateLocale) : '' },
    textColumnRule('supplier_name'),
    exactColumnRule('status', (r) => r.status),
  ], [dateLocale]);

  const filteredLots = useMemo(
    () => applyColumnFilters(baseFilteredLots, colFilters, columnFilterRules),
    [baseFilteredLots, colFilters, columnFilterRules],
  );
  const visibleLots = useMemo(
    () => sliceInventaireListItems(filteredLots, itemsPerPage),
    [filteredLots, itemsPerPage],
  );
  useArticlesConfig();
  const generateLotNumber = useCallback(
    () => nextInventaireEntityRef(settings, 'lot', 'LOT', lots, (l) => l.lot_number),
    [settings, lots],
  );

  const openNewLotModal = () => {
    if (!inventoryConfig.enableExpiryTracking) {
      window.alert(p('notice_expiry_disabled', { defaultValue: 'تتبع انتهاء الصلاحية معطّل من إعدادات المخزون.' }));
      return;
    }
    setEditingRecord(null);
    setLotFormTab('basicInfo');
    setFormData({
      lot_number: inventoryConfig.autoGenerateLotReference !== false ? generateLotNumber() : '',
      product_id: '',
      product_name: '',
      quantity: '',
      unit: 'قطعة',
      manufacturing_date: null,
      expiry_date: null,
      supplier_id: '',
      supplier_name: '',
      reception_date: null,
      location_id: '',
      status: 'active',
      quality_status: 'approved',
      batch_number: '',
      serial_number: '',
      certificate_number: '',
      origin_country: '',
      notes: '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const lotNumber = (formData.lot_number || '').trim() || generateLotNumber();
      const submitData = { 
        lot_number: lotNumber,
        product_id: parseInt(formData.product_id) || null,
        product_name: formData.product_name,
        quantity: parseFloat(formData.quantity) || 0,
        unit: formData.unit,
        manufacturing_date: formData.manufacturing_date ? new Date(formData.manufacturing_date).toISOString() : null,
        expiry_date: formData.expiry_date ? new Date(formData.expiry_date).toISOString() : null,
        supplier_id: parseInt(formData.supplier_id) || null,
        supplier_name: formData.supplier_name,
        reception_date: formData.reception_date ? new Date(formData.reception_date).toISOString() : null,
        location_id: parseInt(formData.location_id) || null,
        status: formData.status,
        quality_status: formData.quality_status,
        batch_number: formData.batch_number,
        serial_number: formData.serial_number,
        certificate_number: formData.certificate_number,
        origin_country: formData.origin_country,
        notes: formData.notes || ''
      };
      
      if (editingRecord) {
        await localApiAdapter.put(`/inventory/lots/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'الدفعة' }));
      } else {
        await localApiAdapter.post('/inventory/lots', submitData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'الدفعة' }));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('alert_save_error', { message: error.message || p('alert_unknown_error') }));
      return;
    } finally {
      resetForm();
      loadLots();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ 
      lot_number: record.lot_number || '',
      product_id: record.product_id?.toString() || '',
      product_name: record.product_name || '',
      quantity: record.quantity?.toString() || '',
      unit: record.unit || 'kg',
      manufacturing_date: record.manufacturing_date ? new Date(record.manufacturing_date) : null,
      expiry_date: record.expiry_date ? new Date(record.expiry_date) : null,
      supplier_id: record.supplier_id?.toString() || '',
      supplier_name: record.supplier_name || '',
      reception_date: record.reception_date ? new Date(record.reception_date) : null,
      location_id: record.location_id?.toString() || '',
      status: record.status || 'active',
      quality_status: record.quality_status || 'approved',
      batch_number: record.batch_number || '',
      serial_number: record.serial_number || '',
      certificate_number: record.certificate_number || '',
      origin_country: record.origin_country || '',
      notes: record.notes || ''
    });
    setLotFormTab('basicInfo');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/inventory/lots/${id}`);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'الدفعة' }));
        loadLots();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_save_error', { message: error.message || p('alert_unknown_error') }));
      }
    }
  };

  const filteredLotsIds = useMemo(() => filteredLots.map((r) => r.id), [filteredLots]);
  const allRowsSelected = filteredLotsIds.length > 0 && filteredLotsIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredLotsIds);
    }
  };
  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]));
  };
  const handleBulkDelete = async () => {
    if (window.confirm(p('delete_bulk_confirm', { count: selectedRecords.length }))) {
      try {
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/inventory/lots/${id}`)));
        notifyBulkDeleted(showNotify, selectedRecords.length, p('entity_label', { defaultValue: 'الدفعة' }));
        setSelectedRecords([]);
        loadLots();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('alert_save_error', { message: error.message || p('alert_unknown_error') }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      lot_number: '', 
      product_id: '', 
      product_name: '',
      quantity: '',
      unit: 'kg',
      manufacturing_date: null,
      expiry_date: null,
      supplier_id: '',
      supplier_name: '',
      reception_date: null,
      location_id: '',
      status: 'active',
      quality_status: 'approved',
      batch_number: '',
      serial_number: '',
      certificate_number: '',
      origin_country: '',
      notes: ''
    });
    setEditingRecord(null);
    setLotFormTab('basicInfo');
    setShowModal(false);
  };


  // Export handlers
  const handleExport = () => {
    exportToCSV(filteredLots, 'lots_tracabilite', [
      { key: 'lot_number', label: p('export_col_lot') },
      { key: 'product_name', label: p('export_col_product') },
      { key: 'quantity', label: p('export_col_quantity') },
      { key: 'unit', label: p('export_col_unit') },
      { key: 'expiry_date', label: p('export_col_expiry') },
      { key: 'supplier_name', label: p('export_col_supplier') },
      { key: 'status', label: p('export_col_status') }
    ]);
  };

  // Export PDF
  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      
      const filteredData = filteredLots;
      
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `
        <div class="report-content">
          <table>
            <thead>
              <tr>
                <th>${p('export_col_lot')}</th>
                <th>${p('export_col_product')}</th>
                <th>${p('export_col_quantity')}</th>
                <th>${p('col_expiration')}</th>
                <th>${p('export_col_supplier')}</th>
                <th>${p('export_col_status')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(item => `
                <tr>
                  <td>${item.lot_number || p('na')}</td>
                  <td>${item.product_name || p('na')}</td>
                  <td>${item.quantity || 0} ${item.unit || ''}</td>
                  <td>${item.expiry_date ? formatDate(item.expiry_date) : p('na')}</td>
                  <td>${item.supplier_name || p('na')}</td>
                  <td>${item.status || p('na')}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
      
      document.body.appendChild(tempDiv);
      await generateReportPDF(p('report_pdf_title'), {});
      document.body.removeChild(tempDiv);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(p('alert_pdf_error'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Export Excel (Advanced)
  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      
      const filteredData = filteredLots;
      
      const columns = [
        { key: 'lot_number', label: p('export_col_lot'), width: 20 },
        { key: 'product_name', label: p('export_col_product'), width: 25 },
        { key: 'quantity', label: p('export_col_quantity'), width: 12 },
        { key: 'unit', label: p('export_col_unit'), width: 10 },
        { key: 'manufacturing_date', label: p('export_col_manufacturing'), width: 15 },
        { key: 'expiry_date', label: p('export_col_expiry'), width: 15 },
        { key: 'supplier_name', label: p('export_col_supplier'), width: 20 },
        { key: 'batch_number', label: p('export_col_batch'), width: 15 },
        { key: 'quality_status', label: p('export_col_quality'), width: 12 },
        { key: 'status', label: p('export_col_status'), width: 12 }
      ];
      
      const data = filteredData.map(item => ({
        lot_number: item.lot_number || '',
        product_name: item.product_name || '',
        quantity: item.quantity || 0,
        unit: item.unit || '',
        manufacturing_date: item.manufacturing_date ? formatDate(item.manufacturing_date) : '',
        expiry_date: item.expiry_date ? formatDate(item.expiry_date) : '',
        supplier_name: item.supplier_name || '',
        batch_number: item.batch_number || '',
        quality_status: getQualityStatusLabel(item.quality_status),
        status: item.status || ''
      }));
      
      await generateReportExcel('lots_report', p('report_excel_title'), {
        sheetName: p('report_excel_sheet'),
      });
      
    } catch (error) {
      console.error('Error generating Excel:', error);
      alert(p('alert_excel_error'));
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const statusOptions = [
    { value: 'active', label: p('status_active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'expired', label: p('status_expired'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
    { value: 'recalled', label: p('status_recalled'), icon: AlertTriangle, style: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
    { value: 'quarantine', label: p('status_quarantine'), icon: Shield, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' }
  ];

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/inventory/lots/${recordId}`, { status: newStatus });
      await loadLots();
    } catch (error) {
      console.error('❌ Error updating status:', error);
    }
  };


  const getExpiryDaysRemaining = (expiryDate) => {
    if (!expiryDate) return null;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getExpiryBadge = (expiryDate) => {
    const daysRemaining = getExpiryDaysRemaining(expiryDate);
    if (daysRemaining === null) return <span className="text-white/40 text-xs">{p('na')}</span>;
    
    if (daysRemaining < 0) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30">{p('expiry_expired')}</span>;
    } else if (daysRemaining === 0) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30">{p('expiry_today')}</span>;
    } else if (daysRemaining <= 7) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30">{p('expiry_days_remaining', { days: daysRemaining })}</span>;
    } else if (daysRemaining <= expiryAlertDays) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">{p('expiry_days_remaining', { days: daysRemaining })}</span>;
    } else {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">{p('expiry_days_remaining', { days: daysRemaining })}</span>;
    }
  };

  const INVENTAIRE_TABLE_COLUMNS = [
    { key: 'lot_number', labelKey: 'col_lot' },
    { key: 'product_name', labelKey: 'col_product' },
    { key: 'quantity', labelKey: 'col_quantity' },
    { key: 'expiry_date', labelKey: 'col_expiration' },
    { key: 'supplier_name', labelKey: 'col_supplier' },
    { key: 'status', labelKey: 'col_status' },
    { key: 'actions', labelKey: 'col_actions', required: true },
  ];

  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildInventaireColumnVisibilityState(INVENTAIRE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildInventaireQawaemColumnDefs(INVENTAIRE_TABLE_COLUMNS, p, columnVisibility),
    [p, columnVisibility],
  );
  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isInventaireColumnVisible(columnVisibility, key);
  const tableColSpan = inventaireTableColSpan(columnVisibility);

  const inventaireStatEntries = useMemo(() => sliceQawaemStats([
    { key: 'stat_total', label: p('stat_total'), value: stats.total, icon: Package },
    { key: 'stat_active', label: p('stat_active'), value: stats.active, icon: CheckCircle },
    { key: 'stat_expiring', label: p('stat_expiring'), value: stats.expiringToday, icon: AlertTriangle },
    { key: 'stat_expired', label: p('stat_expired'), value: stats.expired, icon: XCircle },
  ], 4), [stats, p]);


  const handleImport = () => {
    triggerFileInput((file) => {
      importFromFile(file, () => loadLots());
    });
  };

  const importExportProps = useMemo(() => buildInventaireImportExportProps({
    showImportDropdown,
    setShowImportDropdown,
    showExportDropdown,
    setShowExportDropdown,
    importDropdownRef,
    exportDropdownRef,
    importLabel: t('common.import'),
    exportLabel: t('common.export'),
    generatingLabel: p('generating'),
    isGeneratingPDF,
    handlers: {
      onImport: handleImport,
      onExportCSV: handleExport,
      onExportPDF: handleExportPDF,
      onExportExcel: handleExportExcel,
    },
  }), [showImportDropdown, showExportDropdown, isGeneratingPDF, t, p]);

  return (
    <>
    <InventoryConfigNotice
      message={!inventoryConfig.enableExpiryTracking ? p('notice_expiry_disabled_banner', { defaultValue: 'تتبع انتهاء الصلاحية معطّل — فعّله من إعدادات المخزون ← انتهاء الصلاحية.' }) : null}
    />
    <InventaireListPageShell
      title={p('title')}
      subtitle={p('subtitle')}
      icon={Package}
      statEntries={inventaireStatEntries}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder')}
      onCreate={openNewLotModal}
      createLabel={p('new_lot')}
      importExport={importExportProps}
      filterActions={buildInventaireBulkDeleteFilterAction({ count: selectedRecords.length, onDelete: handleBulkDelete, p })}
    >
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <InventaireTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onSelectAll={handleSelectAll} p={p} />
                  
                  {isColVisible('lot_number') && (

                  
                    <TableColumnHeaderFilter columnKey="lot_number" label={p('col_lot')} value={colFilters.lot_number ?? ''} onChange={(v) => setColFilter('lot_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('product_name') && (

                    <TableColumnHeaderFilter columnKey="product_name" label={p('col_product')} value={colFilters.product_name ?? ''} onChange={(v) => setColFilter('product_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('quantity') && (

                    <TableColumnHeaderFilter columnKey="quantity" label={p('col_quantity')} value={colFilters.quantity ?? ''} onChange={(v) => setColFilter('quantity', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('expiry_date') && (

                    <TableColumnHeaderFilter columnKey="expiry_date" label={p('col_expiration')} value={colFilters.expiry_date ?? ''} onChange={(v) => setColFilter('expiry_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('supplier_name') && (

                    <TableColumnHeaderFilter columnKey="supplier_name" label={p('col_supplier')} value={colFilters.supplier_name ?? ''} onChange={(v) => setColFilter('supplier_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('status') && (

                    <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={statusOptions.map((s) => ({ value: s.value, label: s.label }))} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName={INVENTAIRE_TABLE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {visibleLots.length > 0 ? (
                  visibleLots.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <InventaireTableRowCheckbox checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} />
                      
                      {isColVisible('lot_number') && (

                      
                        <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold flex items-center gap-2">
                            <Barcode size={16} className="text-blue-400" />
                            {record.lot_number}
                          </span>
                          {record.batch_number && <span className="text-white/50 text-xs mt-0.5">{p('label_batch')}: {record.batch_number}</span>}
                        </div>
                      </td>

                      
                      )}
                      
                      {isColVisible('product_name') && (

                      
                        <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.product_name}</span>
                          {record.serial_number && <span className="text-white/50 text-xs mt-0.5">{p('label_serial')}: {record.serial_number}</span>}
                        </div>
                      </td>

                      
                      )}
                      
                      {isColVisible('quantity') && (

                      
                        <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.quantity || 0} {record.unit || ''}</span>
                        </div>
                      </td>

                      
                      )}
                      
                      {isColVisible('expiry_date') && (

                      
                        <td className="px-6 py-4">
                        <div className="flex flex-col gap-1">
                          {record.expiry_date && <span className="text-white/70 text-sm">{formatDate(record.expiry_date)}</span>}
                          {getExpiryBadge(record.expiry_date)}
                        </div>
                      </td>

                      
                      )}
                      
                      {isColVisible('supplier_name') && (

                      
                        <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.supplier_name || p('na')}</span>
                          {record.origin_country && <span className="text-white/50 text-xs mt-0.5">{record.origin_country}</span>}
                        </div>
                      </td>

                      
                      )}
                      
                      {isColVisible('status') && (

                      
                        <td className="px-6 py-4 text-center"><div className="flex justify-center"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} /></div></td>

                      
                      )}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><Package size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {visibleLots.length > 0 ? (
              visibleLots.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Barcode size={20} className="text-blue-400" />
                          {record.lot_number}
                        </h3>
                        <div className="flex"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} /></div>
                        {getExpiryBadge(record.expiry_date)}
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_product')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_name')}:</span> {record.product_name || p('na')}</div>
                            <div><span className="font-medium">{p('label_quantity')}:</span> {record.quantity || 0} {record.unit || ''}</div>
                            {record.serial_number && <div><span className="font-medium">{p('label_serial')}:</span> {record.serial_number}</div>}
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_traceability')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            {record.batch_number && <div><span className="font-medium">{p('label_batch')}:</span> {record.batch_number}</div>}
                            {record.certificate_number && <div><span className="font-medium">{p('label_certificate')}:</span> {record.certificate_number}</div>}
                            <div><span className="font-medium">{p('label_quality')}:</span> {getQualityStatusLabel(record.quality_status)}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_dates')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            {record.manufacturing_date && <div><span className="font-medium">{p('label_manufacturing')}:</span> {formatDate(record.manufacturing_date)}</div>}
                            {record.expiry_date && <div><span className="font-medium">{p('label_expiration')}:</span> {formatDate(record.expiry_date)}</div>}
                            {record.reception_date && <div><span className="font-medium">{p('label_reception')}:</span> {formatDate(record.reception_date)}</div>}
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_supplier')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_name')}:</span> {record.supplier_name || p('na')}</div>
                            {record.origin_country && <div><span className="font-medium">{p('label_origin')}:</span> {record.origin_country}</div>}
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_notes')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <p>{record.notes || p('no_notes')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-1 ml-4 shrink-0">
                      <QawaemErpRowActions
                        compact
                        size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Package size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty')}</p>
              </div>
            )}
          </div>
        )}
    </InventaireListPageShell>

      {/* View Modal */}
      {showViewModal && viewingRecord && ReactDOM.createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={() => setShowViewModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
              style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('view_title')}</h2>
                <button onClick={() => setShowViewModal(false)} className="text-white/70 hover:text-white transition-colors">
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Barcode size={18} className="text-blue-400" />
                    {p('section_identification')}
                  </h3>
                  <div className={INVENTAIRE_FORM_GRID}>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_lot')}</p>
                      <p className="text-white font-medium text-lg">{viewingRecord.lot_number}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_batch_number')}</p>
                      <p className="text-white font-medium">{viewingRecord.batch_number || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_serial_number')}</p>
                      <p className="text-white font-medium">{viewingRecord.serial_number || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_status')}</p>
                      <div className="flex"><QawaemClickableStatusBadge status={viewingRecord.status} recordId={viewingRecord.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} /></div>
                    </div>
                  </div>
                </div>

                <div className={INVENTAIRE_FORM_GRID}>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Box size={18} className="text-green-400" />
                      {p('section_product')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_name')}</p>
                        <p className="text-white font-medium">{viewingRecord.product_name}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_quantity')}</p>
                        <p className="text-white font-medium">{viewingRecord.quantity || 0} {viewingRecord.unit || ''}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Clock size={18} className="text-yellow-400" />
                      {p('section_dates')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_manufacturing')}</p>
                        <p className="text-white font-medium">{viewingRecord.manufacturing_date ? formatDate(viewingRecord.manufacturing_date) : p('na')}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_expiration')}</p>
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium">{viewingRecord.expiry_date ? formatDate(viewingRecord.expiry_date) : p('na')}</p>
                          {getExpiryBadge(viewingRecord.expiry_date)}
                        </div>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_reception')}</p>
                        <p className="text-white font-medium">{viewingRecord.reception_date ? formatDate(viewingRecord.reception_date) : p('na')}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={INVENTAIRE_FORM_GRID}>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Tag size={18} className="text-purple-400" />
                      {p('section_supplier')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_name')}</p>
                        <p className="text-white font-medium">{viewingRecord.supplier_name || p('na')}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_origin_country')}</p>
                        <p className="text-white font-medium">{viewingRecord.origin_country || p('na')}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Shield size={18} className="text-red-400" />
                      {p('section_quality')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_quality_status')}</p>
                        <p className="text-white font-medium">{getQualityStatusLabel(viewingRecord.quality_status)}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_certificate_number')}</p>
                        <p className="text-white font-medium">{viewingRecord.certificate_number || p('na')}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <FileText size={18} className="text-blue-400" />
                    {p('section_notes')}
                  </h3>
                  <p className="text-white/80">{viewingRecord.notes || p('no_notes')}</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* Edit/Create Modal */}
      {showModal && (
        <InventaireModalPortal show={showModal} onClose={resetForm} maxWidth="1200px">
            <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0">
              <h2 className="text-2xl font-bold text-white">{editingRecord ? p('edit_lot') : p('create_lot')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="lot-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30">
                  <Check size={18} />
                  {editingRecord ? p('update') : p('create')}
                </button>
                <button onClick={resetForm} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>
            
            <FacturationFormModalTabs
              tabs={lotFormTabs}
              activeTab={lotFormTab}
              onTabChange={setLotFormTab}
              isLight={isDashboardLightMode}
              ariaLabel={editingRecord ? p('edit_lot') : p('create_lot')}
            />

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="lot-form" onSubmit={handleSubmit}>
              {lotFormTab === 'basicInfo' && (
              <div className={INVENTAIRE_FORM_GRID}><div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_lot_number')}</label>
                            <input type="text" required value={formData.lot_number} onChange={(e) => setFormData({ ...formData, lot_number: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono" placeholder={p('placeholder_lot_ref', { defaultValue: 'LOT-001' })} dir="ltr" />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_batch_number')}</label>
                            <input type="text" value={formData.batch_number} onChange={(e) => setFormData({ ...formData, batch_number: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_batch')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_serial_number')}</label>
                            <input type="text" value={formData.serial_number} onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_serial')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_product')}</label>
                            <CustomSelect
                              value={formData.product_id}
                              onChange={(value) => {
                                const selectedProduct = products.find(prod => prod.id === value);
                                setFormData({ 
                                  ...formData, 
                                  product_id: value,
                                  product_name: selectedProduct ? (selectedProduct.name || selectedProduct.product_name) : ''
                                });
                              }}
                              options={products.map(prod => ({
                                value: prod.id,
                                label: `${prod.name || prod.product_name} ${prod.reference ? `(${prod.reference})` : ''}`
                              }))}
                              placeholder={p('placeholder_select_product')}
                              searchable={true}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_quantity_field')}</label>
                            <CustomNumberInput
                              value={formData.quantity}
                              onChange={(value) => setFormData({ ...formData, quantity: value })}
                              placeholder="0"
                              step={0.01}
                              min={0}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_unit_field')}</label>
                            <CustomSelect
                              value={formData.unit}
                              onChange={(value) => setFormData({ ...formData, unit: value })}
                              options={[
                                { value: 'kg', label: p('unit_kg') },
                                { value: 'g', label: p('unit_g') },
                                { value: 'L', label: p('unit_l') },
                                { value: 'ml', label: p('unit_ml') },
                                { value: 'pcs', label: p('unit_pcs') },
                                { value: 'box', label: p('unit_box') }
                              ]}
                              placeholder={p('placeholder_select_unit')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_status_field')}</label>
                            <CustomSelect
                              value={formData.status}
                              onChange={(value) => setFormData({ ...formData, status: value })}
                              options={statusOptions.map(s => ({ value: s.value, label: s.label, icon: <s.icon size={16} /> }))}
                              placeholder={p('placeholder_select_status')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_quality_status')}</label>
                            <CustomSelect
                              value={formData.quality_status}
                              onChange={(value) => setFormData({ ...formData, quality_status: value })}
                              options={[
                                { value: 'approved', label: p('quality_approved') },
                                { value: 'pending', label: p('quality_pending') },
                                { value: 'rejected', label: p('quality_rejected') },
                                { value: 'quarantine', label: p('quality_quarantine') }
                              ]}
                              placeholder={p('placeholder_quality_status')}
                            />
                          </div></div>
              )}

              {lotFormTab === 'traceabilityInfo' && (
              <div className={INVENTAIRE_FORM_GRID}><div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_manufacturing')}</label>
                            <CustomDatePicker
                              value={formData.manufacturing_date}
                              onChange={(date) => setFormData({ ...formData, manufacturing_date: date })}
                              placeholder={p('placeholder_manufacturing')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_expiration')}</label>
                            <CustomDatePicker
                              value={formData.expiry_date}
                              onChange={(date) => setFormData({ ...formData, expiry_date: date })}
                              placeholder={p('placeholder_expiration')}
                              minDate={formData.manufacturing_date}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_reception')}</label>
                            <CustomDatePicker
                              value={formData.reception_date}
                              onChange={(date) => setFormData({ ...formData, reception_date: date })}
                              placeholder={p('placeholder_reception')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_supplier')}</label>
                            <CustomSelect
                              value={formData.supplier_id}
                              onChange={(value) => {
                                setFormData({
                                  ...formData,
                                  ...mapInventaireSupplierSelection(value, suppliers),
                                });
                              }}
                              options={[
                                { value: '', label: p('none_option') },
                                ...buildInventaireSupplierSelectOptions(suppliers),
                              ]}
                              placeholder={p('placeholder_select_supplier')}
                              searchable={true}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_origin_country')}</label>
                            <CustomSelect
                              value={formData.origin_country}
                              onChange={(value) => setFormData({ ...formData, origin_country: value })}
                              options={originCountryOptions}
                              placeholder={p('placeholder_select_origin_country')}
                              searchable={true}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_certificate_number')}</label>
                            <input type="text" value={formData.certificate_number} onChange={(e) => setFormData({ ...formData, certificate_number: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_certificate')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">
                              {locations.length > 0 ? p('label_location') : p('label_warehouse')}
                            </label>
                            <CustomSelect
                              value={formData.location_id}
                              onChange={(value) => setFormData({ ...formData, location_id: value })}
                              options={[
                                { value: '', label: p('none_option') },
                                ...(locations.length > 0
                                  ? locations.map(loc => ({
                                      value: loc.id,
                                      label: `${loc.location_code} - ${loc.location_name} ${warehouses.find(w => w.id === loc.warehouse_id)?.name ? `(${warehouses.find(w => w.id === loc.warehouse_id).name})` : ''}`
                                    }))
                                  : warehouses.map(w => ({
                                      value: w.id,
                                      label: `${w.name} ${w.code ? `(${w.code})` : ''}`
                                    }))
                                )
                              ]}
                              placeholder={locations.length > 0 ? p('placeholder_select_location') : p('placeholder_select_warehouse')}
                              searchable={true}
                            />
                          </div>
                          <div className="md:col-span-3">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('section_notes')}</label>
                            <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={4} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_notes')} />
                          </div></div>
              )}
              </form>
            </div>
        </InventaireModalPortal>
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default LotsTracabilitePage;

